import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ComponentPropsWithoutRef, ElementRef, HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { cn } from './utils';

const Offcanvas = DialogPrimitive.Root;
const OffcanvasTrigger = DialogPrimitive.Trigger;
const OffcanvasPortal = DialogPrimitive.Portal;
const OffcanvasClose = DialogPrimitive.Close;

const OffcanvasOverlay = forwardRef<
    ElementRef<typeof DialogPrimitive.Overlay>,
    ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn('nexus-offcanvas-overlay', className)}
        {...props}
    />
));
OffcanvasOverlay.displayName = 'OffcanvasOverlay';

const OffcanvasContent = forwardRef<
    ElementRef<typeof DialogPrimitive.Content>,
    ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <OffcanvasPortal>
        <OffcanvasOverlay />
        <DialogPrimitive.Content
            ref={ref}
            className={cn('nexus-offcanvas', className)}
            onPointerDownOutside={(e) => {
                const target = e.target as HTMLElement | null;
                if (target?.closest?.('.nx-cidade-results, .nx-async-select-dropdown, [role="listbox"]')) {
                    e.preventDefault();
                }
            }}
            onInteractOutside={(e) => {
                const target = e.target as HTMLElement | null;
                if (target?.closest?.('.nx-cidade-results, .nx-async-select-dropdown, [role="listbox"]')) {
                    e.preventDefault();
                }
            }}
            {...props}
        >
            {children}
            <DialogPrimitive.Close className="nexus-offcanvas-close" aria-label="Fechar">
                ×
            </DialogPrimitive.Close>
        </DialogPrimitive.Content>
    </OffcanvasPortal>
));
OffcanvasContent.displayName = 'OffcanvasContent';

function OffcanvasHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
    return <div className={cn('nexus-offcanvas-header', className)} {...props} />;
}

function OffcanvasBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
    return <div className={cn('nexus-offcanvas-body', className)} {...props} />;
}

function OffcanvasFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
    return <div className={cn('nexus-offcanvas-footer', className)} {...props} />;
}

const OffcanvasTitle = forwardRef<
    ElementRef<typeof DialogPrimitive.Title>,
    ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn('nexus-offcanvas-title', className)}
        {...props}
    />
));
OffcanvasTitle.displayName = 'OffcanvasTitle';

const OffcanvasDescription = forwardRef<
    ElementRef<typeof DialogPrimitive.Description>,
    ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn('nexus-offcanvas-description', className)}
        {...props}
    />
));
OffcanvasDescription.displayName = 'OffcanvasDescription';

export {
    Offcanvas,
    OffcanvasTrigger,
    OffcanvasContent,
    OffcanvasHeader,
    OffcanvasBody,
    OffcanvasFooter,
    OffcanvasTitle,
    OffcanvasDescription,
    OffcanvasClose,
    OffcanvasPortal,
    OffcanvasOverlay,
};
